import axios from 'axios';
import { getActiveStoreId, getApiBaseUrl } from '../lib/storefront/storeBootstrap';
import { getGoogleOAuthReturnInfo } from '../lib/storefront/storePath';
import { isAdminDashboardToken } from '../Utils/authToken';

// Base URL for all API calls (AAA Dashboard backend — default port 9090 in development).
// Single source of truth so a missing env var cannot turn into a request to "undefined/api/...".
const API_BASE_URL = getApiBaseUrl();

// Kept only for the StoreProvider's explicit hand-off; every reader below resolves the id
// from the bootstrap module so a request issued before that hand-off still carries a store.
let storefrontStoreId = null;

export function setStorefrontStoreId(storeId) {
    storefrontStoreId = storeId || null;
}

export function getStorefrontStoreId() {
    return getActiveStoreId() || storefrontStoreId;
}

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

const unwrapData = (response) => response?.data?.data ?? response?.data;

const normalizeList = (response) => {
    const data = unwrapData(response);
    if (Array.isArray(data)) {
        return withData(response, data);
    }
    return withData(response, []);
};

const withData = (response, data) => ({
    ...response,
    data,
});

const buildOrderPayload = (orderData, paymentMethod, extraPaymentDetails = {}) => {
    const couponValue = typeof orderData?.coupon === 'string'
        ? orderData.coupon.trim()
        : orderData?.coupon?.code;

    return {
        userId: orderData?.userId,
        personalInfo: orderData?.personalInfo,
        termsAcceptance: Boolean(orderData?.terms),
        items: orderData?.items || [],
        coupon: couponValue ? { code: couponValue } : undefined,
        shippingHandling: orderData?.shippingHandling || 0,
        paymentMethod,
        paymentDetails: {
            ...(orderData?.paymentDetails || {}),
            ...extraPaymentDetails,
        },
    };
};

// Request interceptor to add auth token to requests
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        // An admin dashboard JWT shares localStorage with the storefront on localhost;
        // sending it here would authenticate the shopper as that admin.
        if (token && !isAdminDashboardToken(token)) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const storeId = getStorefrontStoreId();
        if (storeId) {
            config.headers['X-Store-Id'] = storeId;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Token expired or invalid. An admin dashboard token was never sent, so a 401
            // says nothing about it — clearing it would sign the admin out of the dashboard.
            const token = localStorage.getItem('token');
            if (token && !isAdminDashboardToken(token)) {
                localStorage.removeItem('token');
                localStorage.removeItem('userId');
                window.dispatchEvent(new Event('app:auth-cleared'));
            }
        }
        return Promise.reject(error);
    }
);

// ============================================
// Authentication API
// ============================================

export const authAPI = {
    // User login
    login: (credentials) => {
        return apiClient.post('/api/appUser/login', credentials).then((response) => {
            const data = unwrapData(response);
            const user = data?.user || {};
            return withData(response, {
                token: data?.token,
                user: {
                    ...user,
                    _id: user._id || user.id,
                },
            });
        });
    },

    // User registration
    register: (userData) => {
        return apiClient.post('/api/appUser/register', userData).then((response) => {
            const data = unwrapData(response);
            const user = data?.user || {};
            return withData(response, {
                token: data?.token,
                user: {
                    ...user,
                    _id: user._id || user.id,
                },
            });
        });
    },

    // Forgot password
    forgotPassword: (email) => {
        return apiClient.post('/api/appUser/forgot-password', email);
    },

    // Reset password
    resetPassword: (data) => {
        return apiClient.patch('/api/appUser/reset-password', data);
    },

    // Change password
    changePassword: (data) => {
        const userId = localStorage.getItem('userId');
        return apiClient.put(`/api/appUser/users/${userId}`, {
            currentPassword: data.currentPassword,
            newPassword: data.newPassword,
        }).then((response) => withData(response, { msg: response?.data?.message || 'Password updated' }));
    },

    // Get user data
    getUserData: () => {
        return apiClient.get('/api/appUser/user').then((response) => {
            const user = unwrapData(response) || {};
            return withData(response, {
                ...user,
                _id: user._id || user.id,
            });
        });
    },

    // Logout
    logout: () => {
        return apiClient.post('/api/appUser/logout').then((response) =>
            withData(response, { msg: response?.data?.message || 'Logout successful' })
        );
    },

    // Google-only customers (no password yet): email a one-time code, then set the first password
    requestPasswordSetupCode: () => {
        return apiClient.post('/api/appUser/password/setup-code');
    },

    setupPassword: ({ code, newPassword, confirmPassword }) => {
        return apiClient.post('/api/appUser/password/setup', { code, newPassword, confirmPassword });
    },

    // Update user profile
    updateProfile: (userId, profileData) => {
        return apiClient.put(`/api/appUser/users/${userId}`, profileData).then((response) => {
            const data = unwrapData(response);
            return withData(response, {
                message: response?.data?.message || 'Profile updated',
                ...data,
            });
        });
    },

    // Update user profile avatar
    updateAvatar: (userId, file) => {
        const formData = new FormData();
        formData.append('avatar', file);
        return apiClient.put(`/api/appUser/users/${userId}/avatar`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }).then((response) => {
            const data = unwrapData(response) || {};
            return withData(response, {
                avatar: data.avatar,
                message: response?.data?.message,
            });
        });
    },

    // Google OAuth — full-page redirect cannot send X-Store-Id; pass store + return URLs in query.
    // intent: 'login' only signs in existing customers; 'register' only creates new ones.
    googleAuth: ({ intent = 'login' } = {}) => {
        const storeId = getActiveStoreId();
        if (!storeId) {
            throw new Error('Store is not loaded yet. Please refresh the page and try again.');
        }
        const { returnOrigin, returnPath, signInPath, signUpPath } = getGoogleOAuthReturnInfo();
        const params = new URLSearchParams({
            storeId,
            returnOrigin,
            returnPath,
            signInPath,
            signUpPath,
            intent,
        });
        return `${API_BASE_URL}/api/appUser/google?${params.toString()}`;
    },
};

// ============================================
// Products API
// ============================================

export const productsAPI = {
    // Get all products
    getAll: (params) => {
        return apiClient.get('/api/Retail', { params }).then((response) => {
            const d = response?.data;
            if (Array.isArray(d)) return response;
            if (d && Array.isArray(d.products)) {
                return withData(response, d.products);
            }
            // Fallback: keep original response
            return response;
        });
    },

    // Get products with backend pagination metadata
    getList: (params = {}) => {
        return apiClient.get('/api/Retail', { params }).then((response) => {
            const d = response?.data;
            const page = Number.parseInt(params.page, 10) || 1;
            const limit = Number.parseInt(params.limit, 10) || 45;

            if (Array.isArray(d)) {
                const start = (page - 1) * limit;
                const products = d.slice(start, start + limit);
                const totalPages = Math.ceil(d.length / limit) || 1;

                return withData(response, {
                    products,
                    pagination: {
                        currentPage: page,
                        totalPages,
                        totalItems: d.length,
                        hasNextPage: page < totalPages,
                        hasPreviousPage: page > 1,
                    },
                    totals: {
                        totalItemsOverall: d.length,
                        totalActive: d.filter((p) => p?.status === 'active').length,
                        totalInactive: d.filter((p) => p?.status !== 'active').length,
                    },
                });
            }

            if (d && Array.isArray(d.products)) {
                const pagination = d.pagination || {};
                const totalItems = Number(pagination.totalItems ?? pagination.total ?? d.products.length) || 0;
                const totalPages = Math.max(
                    totalItems > 0 ? 1 : 0,
                    Number(pagination.totalPages ?? pagination.pages) || Math.ceil(totalItems / limit) || 0
                );

                return withData(response, {
                    ...d,
                    pagination: {
                        currentPage: Math.min(
                            Math.max(1, Number(pagination.currentPage ?? pagination.page ?? page) || page),
                            totalPages || 1
                        ),
                        totalPages,
                        totalItems,
                        hasNextPage: pagination.hasNextPage ?? page < totalPages,
                        hasPreviousPage: pagination.hasPreviousPage ?? page > 1,
                    },
                });
            }

            return withData(response, {
                products: [],
                pagination: {
                    currentPage: 1,
                    totalPages: 1,
                    totalItems: 0,
                    hasNextPage: false,
                    hasPreviousPage: false,
                },
                totals: {
                    totalItemsOverall: 0,
                    totalActive: 0,
                    totalInactive: 0,
                },
            });
        });
    },

    // Filter facet counts for the public product page
    getFacets: (params = {}) => {
        return apiClient.get('/api/Retail/facets', { params }).then((response) => {
            const data = response?.data;
            return withData(response, data || {});
        });
    },

    // Get product by ID
    getById: (id) => {
        return apiClient.get(`/api/Retail/${id}`);
    },

    // Increment view count (public, fire-and-forget)
    incrementView: (id) => {
        return apiClient.patch(`/api/Retail/${id}/view`);
    },
};

// ============================================
// Orders API
// ============================================

export const ordersAPI = {
    getOrderByNumber: (orderNumber) => {
        return apiClient.get(`/api/order/track/${encodeURIComponent(orderNumber)}`);
    },

    searchByContact: ({ email, phone }) => {
        const params = new URLSearchParams();
        if (email) params.append('email', email);
        if (phone) params.append('phone', phone);
        return apiClient.get(`/api/order/search?${params}`);
    },

    getUserOrderHistory: () => apiClient.get('/api/order/user/history'),

    getUserOrders: (status = null, limit = 20) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (limit) params.append('limit', limit);
        return apiClient.get(`/api/order/user/orders?${params}`);
    },

    cancelUserOrder: (orderId, cancellationReason = 'Cancelled by customer') =>
        apiClient.patch(`/api/order/user/${orderId}/cancel`, { cancellationReason }),

    // Create order with cash on delivery
    createCashOnDelivery: (orderData) => {
        return apiClient.post('/api/order', buildOrderPayload(orderData, 'cod'));
    },

    // Create order paid online. The response's `checkout.redirectUrl` is the provider's
    // payment page; the provider sends the customer back to `returnPath` afterwards.
    createOnline: (orderData, returnPath) => {
        return apiClient.post('/api/order', {
            ...buildOrderPayload(orderData, 'online'),
            returnPath,
        });
    },

    // Create order with IBAN transfer
    createIbanTransfer: (formData) => {
        const receipt = formData?.paymentDetails?.receiptUrl;
        return apiClient.post('/api/order', buildOrderPayload(formData, 'ibanTransfer', {
            receiptName: receipt?.name || null,
        }));
    },
};

// ============================================
// Online payments API
// ============================================

export const paymentsAPI = {
    // Payment status of the signed-in customer's own order
    getOrderStatus: (orderNumber) =>
        apiClient.get(`/api/payments/orders/${encodeURIComponent(orderNumber)}`),

    // New payment page for an order that is still waiting for payment
    retry: (orderNumber, returnPath) =>
        apiClient.post(`/api/payments/orders/${encodeURIComponent(orderNumber)}/retry`, { returnPath }),
};

// ============================================
// Coupons API
// ============================================

export const couponAPI = {
    /**
     * Checks a code before the order is placed. The server re-validates and
     * re-prices it on submit, so what comes back here is a preview only.
     * orderType is omitted: product orders consume coupons without one, and
     * sending it would reject codes restricted to delivery/pickup order types.
     */
    validate: ({ code, subtotal }) => {
        return apiClient.post('/api/coupons/validate', { code, subtotal }).then((response) => {
            const data = unwrapData(response);
            return withData(response, {
                coupon: data?.coupon || null,
                appliedDiscount: data?.appliedDiscount || null,
                message: response?.data?.message || 'Coupon is valid',
            });
        });
    },
};

// ============================================
// Home/Banner API
// ============================================

export const homeAPI = {
    // Hero carousel slides from /api/hero/items
    getHomeItems: () => {
        return apiClient.get('/api/hero/items').then(normalizeList);
    },

    // Get banner data
    getBanner: () => {
        return apiClient.get('/api/banner').then(normalizeList);
    },

    // Photo gallery from /api/photo-gallery
    getPhotoGallery: () => {
        return apiClient.get('/api/photo-gallery').then(normalizeList);
    },
};

// ============================================
// Contact API
// ============================================

export const contactAPI = {
    sendMessage: (payload) => {
        return apiClient.post('/api/contact-message', payload).then((response) => {
            const data = unwrapData(response);
            return withData(response, {
                message: response?.data?.message || data?.message || 'Contact message sent successfully',
                data,
            });
        });
    },
};

// ============================================
// Subscriber API
// ============================================

export const subscriberAPI = {
    subscribe: (email, source = 'footer') => {
        return apiClient.post('/api/subscriber/subscribe', { email, source }).then((response) => {
            const data = unwrapData(response);
            return withData(response, {
                message: response?.data?.message || data?.message || 'Subscribed successfully',
                data,
            });
        });
    },

    // Target of the "Unsubscribe" link in newsletter emails.
    unsubscribe: (email) => {
        return apiClient.post('/api/subscriber/unsubscribe', { email }).then((response) => {
            const data = unwrapData(response);
            return withData(response, {
                message: response?.data?.message || data?.message || 'Unsubscribed successfully',
                data,
            });
        });
    },
};

// ============================================
// Export default API client for custom requests
// ============================================

export default apiClient;
